import React, { useState, useCallback } from 'react';
import Cropper from 'react-easy-crop';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Slider } from '../ui/slider';
import { Loader2, ZoomIn } from 'lucide-react';

const OUT_W = 1200;
const OUT_H = 675;

const cropToFile = (src, area, name) => new Promise((resolve, reject) => {
  const img = new window.Image();
  img.onload = () => {
    const canvas = document.createElement('canvas');
    canvas.width = OUT_W;
    canvas.height = OUT_H;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, OUT_W, OUT_H);
    ctx.drawImage(img, area.x, area.y, area.width, area.height, 0, 0, OUT_W, OUT_H);
    canvas.toBlob((blob) => {
      if (!blob) return reject(new Error('Falha ao gerar imagem'));
      const base = (name || 'imagem').replace(/\.[^.]+$/, '');
      resolve({ file: new File([blob], `${base}.jpg`, { type: 'image/jpeg' }), sourceWidth: area.width });
    }, 'image/jpeg', 0.9);
  };
  img.onerror = () => reject(new Error('Não foi possível ler a imagem'));
  img.src = src;
});

export const ImageCropDialog = ({ src, fileName, onCancel, onConfirm }) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [area, setArea] = useState(null);
  const [processing, setProcessing] = useState(false);

  const onCropComplete = useCallback((_, pixels) => setArea(pixels), []);

  const handleConfirm = async () => {
    if (!area) return;
    setProcessing(true);
    try {
      onConfirm(await cropToFile(src, area, fileName));
    } finally {
      setProcessing(false);
    }
  };

  return (
    <Dialog open={!!src} onOpenChange={(o) => !o && onCancel()}>
      <DialogContent className="max-w-2xl" data-testid="image-crop-dialog">
        <DialogHeader>
          <DialogTitle>Ajustar imagem (16:9)</DialogTitle>
          <DialogDescription>Arraste para posicionar e use o zoom. A imagem final terá 1200 x 675 px.</DialogDescription>
        </DialogHeader>
        <div className="relative w-full h-72 bg-slate-900 rounded-lg overflow-hidden" data-testid="image-crop-area">
          {src && (
            <Cropper
              image={src}
              crop={crop}
              zoom={zoom}
              aspect={16 / 9}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
            />
          )}
        </div>
        <div className="flex items-center gap-3">
          <ZoomIn className="w-4 h-4 text-slate-500" />
          <Slider
            value={[zoom]}
            min={1}
            max={3}
            step={0.05}
            onValueChange={(v) => setZoom(v[0])}
            data-testid="image-crop-zoom"
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel} disabled={processing} data-testid="image-crop-cancel-btn">Cancelar</Button>
          <Button onClick={handleConfirm} disabled={processing || !area} className="bg-cyan-600 hover:bg-cyan-700" data-testid="image-crop-confirm-btn">
            {processing && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Recortar e enviar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
